import type { Device, Room } from "@/lib/api";

/**
 * Rooms the resident still walks through.
 *
 * An open-plan merge keeps the absorbed room on the record for the audit trail
 * (see the room-merge spec), pointing at the room that absorbed it. The app
 * must never list it: it has no thermostat of its own, its sensor is retired,
 * and asking about it undoes the merge in the resident's head.
 */
export function liveRooms(rooms: Room[]): Room[] {
  return rooms.filter((room) => !room.absorbed_into);
}

/** The rooms merged into `room`, so a screen can say why it counts as one. */
export function absorbedBy(rooms: Room[], room: Room): Room[] {
  if (!room.ha_room_id) {
    return [];
  }
  return rooms.filter((other) => other.absorbed_into === room.ha_room_id);
}

/** A room's devices minus the ones a merge retired. */
export function activeDevices(room: Room): Device[] {
  return (room.devices ?? []).filter((device) => !device.decommissioned);
}

export interface RetiredSensor {
  /** The name printed on the sensor's front: the room it was packed for. */
  label: string;
  /** The live room that space now belongs to, when the record says so. */
  survivor: Room | null;
}

/**
 * Sensors a merge retired, wherever the record keeps them.
 *
 * `merge_rooms` leaves the retired sensor on the absorbed room's record (that is
 * how Beta House 3 is stored), so the absorbed rooms are where to look; a
 * decommissioned sensor on a live room is covered too.
 */
export function retiredSensors(rooms: Room[]): RetiredSensor[] {
  const liveById = new Map(
    liveRooms(rooms).flatMap((room) => (room.ha_room_id ? [[room.ha_room_id, room] as const] : [])),
  );
  // One card per physical sensor: a record that carries the same retired unit
  // on two rooms (a hand-fixed merge, say) must not produce two. Serials are
  // the only identity; unserialled devices are never collapsed.
  const seen = new Set<string>();
  return rooms.flatMap((room) =>
    (room.devices ?? [])
      .filter((device) => device.device_type === "AirthingsSensor" && device.decommissioned)
      .filter((device) => {
        // Only a serial identifies a physical unit. Without one, two retired
        // sensors that happen to share a name are still two sensors.
        const serial = device.external_id?.trim();
        if (!serial) {
          return true;
        }
        if (seen.has(serial)) {
          return false;
        }
        seen.add(serial);
        return true;
      })
      .map((device) => ({
        // On an absorbed room the room name is the label. A retired sensor
        // kept on a live room was packed for some other room, so its label is
        // in the device name, which the mirror writes as "<Room> air sensor".
        label: room.absorbed_into
          ? room.name
          : device.name.replace(/ air sensor$/i, "").trim() || room.name,
        survivor: room.absorbed_into ? (liveById.get(room.absorbed_into) ?? null) : room,
      })),
  );
}
